import React from 'react';
import { DollarSign, Package, CheckCircle, MapPin, AlertCircle } from 'lucide-react';
import type { ClaimItem } from '@/lib/claimStore';

interface KpiCardsProps {
  items: ClaimItem[];
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export default function KpiCards({ items }: KpiCardsProps) {
  const totalValue = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
  const pricedCount = items.filter((i) => i.priceStatus === 'priced' || i.priceStatus === 'verified').length;
  const unpricedCount = items.filter((i) => i.priceStatus === 'unpriced').length;
  const rooms = new Set(items.map((i) => i.location)).size;

  return (
    <div className="grid grid-cols-4 gap-4">
      {/* Total Claim Value */}
      <div className="bg-primary rounded-xl p-4 text-primary-foreground relative overflow-hidden">
        <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -translate-y-6 translate-x-6" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign size={14} className="opacity-80" />
            <span className="text-xs font-medium uppercase tracking-widest opacity-80">Total Claim Value</span>
          </div>
          <p className="text-2xl font-bold tabular-nums truncate">{formatCurrency(totalValue)}</p>
          <p className="mt-1 text-xs opacity-70">{items.length} items across {rooms} rooms</p>
        </div>
      </div>

      {/* Items Documented */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <Package size={14} />
          <span className="text-xs font-medium uppercase tracking-wide">Items</span>
        </div>
        <p className="text-2xl font-bold text-foreground tabular-nums">{items.length}</p>
        <p className="text-xs text-muted-foreground">documented</p>
      </div>

      {/* Priced */}
      <div className={`rounded-xl p-4 flex flex-col gap-1 border ${pricedCount === items.length ? 'bg-positive-bg border-positive/20' : 'bg-card border-border'}`}>
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <CheckCircle size={14} className={pricedCount === items.length ? 'text-positive' : ''} />
          <span className="text-xs font-medium uppercase tracking-wide">Priced</span>
        </div>
        <p className={`text-2xl font-bold tabular-nums ${pricedCount === items.length ? 'text-positive' : 'text-foreground'}`}>
          {pricedCount}
          <span className="text-sm font-normal text-muted-foreground">/{items.length}</span>
        </p>
        {unpricedCount > 0 && (
          <p className="text-xs text-negative flex items-center gap-1">
            <AlertCircle size={10} /> {unpricedCount} need research
          </p>
        )}
      </div>

      {/* Rooms Covered */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-muted-foreground mb-1">
          <MapPin size={14} />
          <span className="text-xs font-medium uppercase tracking-wide">Rooms</span>
        </div>
        <p className="text-2xl font-bold text-foreground tabular-nums">{rooms}</p>
        <p className="text-xs text-muted-foreground">locations covered</p>
      </div>
    </div>
  );
}